import {match, Pattern} from 'ts-pattern';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {ConfiguratorFeatureResponseBody} from '@dms/api/new-vehicle';

import {OPTIONAL_INCLUSION} from './newVehicleLabels';

/**
 * Whether the catalogue offers the feature for the selected trim and variant, the assignment
 * applicability the evaluator checks: offered, or why not.
 *
 * An empty `trimInclusions` list means unrestricted, as the package contract spells out, so a
 * catalogue that omits the mapping keeps offering everything instead of nothing.
 */
export const getFeatureOffer = (
  feature: ConfiguratorFeatureResponseBody,
  trimId: string,
  variantId: string
) => {
  const inclusion = feature.trimInclusions.find((item) => item.trimId === trimId);
  // ponytail: a backend from before T20-142113 omits `appliesIf` during rollout; it reads as
  // unrestricted. Drop the fallback once every environment serves it.
  // ponytail: only `anyOfVariantIds` is checked and only for OPTIONAL assignments. The evaluator
  // judges `allOfFeatureIds` against the expanded build, and STANDARD stays trim-wide
  // (`isStandardFeature`); pre-check those here if the catalogue starts using them.
  const variantIds = inclusion?.appliesIf?.anyOfVariantIds ?? [];
  // No variant picked yet (preview can reach this step) leaves the variant unjudged.
  return match({isRestricted: feature.trimInclusions.length > 0, inclusion})
    .with({isRestricted: false}, () => 'offered' as const)
    .with({inclusion: Pattern.nullish}, () => 'notInTrim' as const)
    .with(
      {inclusion: {inclusion: OPTIONAL_INCLUSION}},
      () => isNotNilOrEmpty(variantId) && variantIds.length > 0 && !variantIds.includes(variantId),
      () => 'notForVariant' as const
    )
    .otherwise(() => 'offered' as const);
};
